import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";

const CustomerAcquisitionMetrics = () => {
  const { customerOffersData } = useSelector(
    (state) => state?.merchantReducer
  );

  const [chartData, setChartData] = useState({
    series: [
      { name: "New Visitors", data: [] },
      { name: "First-Time Offer Redeemers", data: [] },
      { name: "Returning Customers", data: [] },
    ],
    categories: [],
  });
  const [maxDataValue, setMaxDataValue] = useState(10); // Initial max value
  const [step, setStep] = useState(2); // Initial step value

  const MONTHS_ORDER = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  const convertMonthToAbbreviation = (monthString) => {
    if (!monthString || typeof monthString !== "string" || !monthString.includes("-")) return "JAN";
    const date = new Date(monthString + "-01");
    if (isNaN(date.getTime())) return "JAN";
    return date.toLocaleString("en-US", { month: "short" }).toUpperCase();
  };

  useEffect(() => {
    if (customerOffersData?.success && customerOffersData?.data?.customerMetrics) {
      const metrics = customerOffersData.data.customerMetrics;

      // Convert months to abbreviations and format the data
      const formattedData = metrics.map((item) => ({
        month: convertMonthToAbbreviation(item.month),
        newVisitors: item.newVisitors || 0,
        firstTimeRedeemers: item.firstTimeRedeemers || 0,
        returningCustomers: item.returningCustomers || 0,
      }));

      // Sort the formatted data based on the predefined month order
      const sortedData = formattedData.sort(
        (a, b) =>
          MONTHS_ORDER.indexOf(a.month) - MONTHS_ORDER.indexOf(b.month)
      );

      const newVisitorsData = sortedData.map((item) => item.newVisitors);
      const firstTimeRedeemersData = sortedData.map((item) => item.firstTimeRedeemers);
      const returningCustomersData = sortedData.map((item) => item.returningCustomers);

      // Calculate max value and step dynamically
      const calculatedMaxDataValue = Math.max(
        ...newVisitorsData,
        ...firstTimeRedeemersData,
        ...returningCustomersData,
        10
      );
      const calculatedStep = Math.ceil(calculatedMaxDataValue / 5);

      setMaxDataValue(calculatedMaxDataValue);
      setStep(calculatedStep);

      // Update chart data
      setChartData({
        series: [
          { name: "New Visitors", data: newVisitorsData },
          { name: "First-Time Offer Redeemers", data: firstTimeRedeemersData },
          { name: "Returning Customers", data: returningCustomersData },
        ],
        categories: sortedData.map((item) => item.month),
      });
    }
  }, [customerOffersData]);

  const options = {
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
      fontFamily: "Poppins",
      fontWeight: 500,
      fontSize: "14px",
    },
    colors: ["#80CAEE", "#49EA80", "#FFC943"],
    chart: {
      fontFamily: "Poppins, sans-serif",
      height: 335,
      type: "area",
      dropShadow: {
        enabled: true,
        color: "#623CEA14",
        top: 10,
        blur: 4,
        left: 0,
        opacity: 0.1,
      },
      toolbar: {
        show: false,
      },
    },
    responsive: [
      {
        breakpoint: 575,
        options: {
          chart: {
            height: 300,
          },
          xaxis: {
            labels: {
              rotate: -45,
              style: {
                fontSize: "10px",
              },
            },
          },
          yaxis: {
            labels: {
              style: {
                fontSize: "10px",
              },
            },
          },
        },
      },
      {
        breakpoint: 1024,
        options: {
          chart: {
            height: 300,
          },
        },
      },
      {
        breakpoint: 1366,
        options: {
          chart: {
            height: 350,
          },
        },
      },
    ],
    stroke: {
      width: 2,
      curve: "smooth",
    },
    grid: {
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 4,
      colors: "#fff",
      strokeColors: ["#80CAEE", "#49EA80", "#FFC943"],
      strokeWidth: 3,
      strokeOpacity: 0.9,
      strokeDashArray: 0,
      fillOpacity: 1,
      hover: {
        size: undefined,
        sizeOffset: 5,
      },
    },
    xaxis: {
      type: "category",
      categories: chartData.categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      title: {
        text: "Time Period (Months)",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
    yaxis: {
      min: 0,
      max: Math.ceil(maxDataValue / step) * step,
      tickAmount: Math.ceil(maxDataValue / step),
      labels: {
        formatter: (value) => value,
      },
      title: {
        text: "Users",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      followCursor: true,
      intersect: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const monthLabel = w.config.xaxis.categories[dataPointIndex] || "";
        const visitors = series[0]?.[dataPointIndex] || 0;
        const redeemers = series[1]?.[dataPointIndex] || 0;
        const returning = series[2]?.[dataPointIndex] || 0;

        return `
          <div class="p-3 font-poppins" style="background: rgba(255, 255, 255, 0.98); border: 1px solid #e2e8f0; border-radius: 6px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); color: #1c2434; font-size: 12px; line-height: 1.5;">
            <div style="font-weight: 600; font-size: 13px; margin-bottom: 8px; color: #1C2434;">${monthLabel}</div>
            <div style="display: flex; flex-direction: column; gap: 4px;">
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: #64748B;">New Visitors:</span>
                <span style="font-weight: 500; color: #3b82f6;">${visitors}</span>
              </div>
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: #64748B;">First-Time Redeemers:</span>
                <span style="font-weight: 500; color: #10b981;">${redeemers}</span>
              </div>
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: #64748B;">Returning Customers:</span>
                <span style="font-weight: 500; color: #f59e0b;">${returning}</span>
              </div>
            </div>
          </div>
        `;
      },
    },
  };

  return (
    <div className="rounded-sm border border-stroke bg-white px-5 pt-7.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 summary offersChart">
      <div className="flex flex-col sm:flex-col xl:flex-row justify-between w-full mt-4">
        <span className="text-[18px] sm:text-[20px] font-poppins font-[600] text-[black] leading-tight flex-1">
          Customer Acquisition Metrics
        </span>
      </div>

      {chartData.series[0]?.data.length > 0 ? (
        <div>
          <div id="chartOne" className="-ml-5 mt-4">
            <ReactApexChart
              options={options}
              series={chartData.series}
              type="area"
              height={350}
            />
          </div>
        </div>
      ) : (
        <div className="w-full h-[200px] flex items-center justify-center">
          <div id="chartTwo" className="min-w-0 md:min-w-[700px]">
            <div className="text-center text-gray-500 text-lg font-medium py-10">
              No New Users Found
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAcquisitionMetrics;
